import {
  ACCOUNTS_PAYABLE_REPORT_VIEW,
  APPOINTMENT_CREATED_BY_REFERRAL_SOURCE_REPORT,
  APPOINTMENT_STATUS_BY_REFERRAL_SOURCE_REPORT,
  COMMISSIONS_BREAKDOWN_REPORT,
  COMMISSIONS_REPORT,
  INSURER_CREDIT_LEDGER,
  MANAGED_CARE_AGING_REPORT,
  PRIVATE_PAY_AGING_REPORT,
  REVENUE_BREAKDOWN_BY_PROVIDER_REPORT
} from "constants/feature-flags";
import { VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS } from "constants/permissions";
import { ADMIN_LEVEL_GROUPS } from "constants/staff";
import { INSURER_CREDIT_LEDGER_GROUPS } from "features/reports/insurer-credit-ledger/insurer-credit-ledger.constants";
import {
  REPORT_IDS,
  REPORT_REGISTRY,
  ReportId,
  isFinancialOnlyAllowedUngovernedPathname,
  reportPath,
  resolveReportIdFromPathname
} from "features/reports/report-registry.config";

import type {
  DestinationAccess,
  QuickLauncherDestination
} from "./destination.types";

const SECTION = "/reports";

/**
 * The Reports side menu's own rules (`reports.component.tsx` `rawMenu`) for a
 * registered report. `featureFlag: null` drops the registry's flag where the
 * menu gates the entry some other way.
 */
export type ReportGate = Pick<
  QuickLauncherDestination,
  "roles" | "permissions" | "when" | "keywords"
> & { featureFlag?: string | null };

const canSeeFinancialReports = ({ currentUser }: DestinationAccess) =>
  Boolean(currentUser?.can_see_manufacturer_cost);
const isAdmin = ({ roles }: DestinationAccess) => roles.isAdminOrBusinessOwner;
const commissionsEnabled = ({ flags }: DestinationAccess) =>
  Boolean(flags[COMMISSIONS_REPORT] || flags[COMMISSIONS_BREAKDOWN_REPORT]);

const ADMIN: ReportGate = { roles: ADMIN_LEVEL_GROUPS };
const ADMIN_FINANCIAL: ReportGate = {
  roles: ADMIN_LEVEL_GROUPS,
  when: canSeeFinancialReports
};
const ADMIN_OR_ANY_REPORTS_PERMISSION: ReportGate = {
  roles: ADMIN_LEVEL_GROUPS,
  permissions: [VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS]
};

export const REPORT_GATES: Record<ReportId, ReportGate> = {
  "patients-report": ADMIN,
  "survey-report": ADMIN,
  "onboarding-forms-report": ADMIN,
  "visit-tracker-report": ADMIN,
  "insurance-policies-report": ADMIN,
  // The Appointments group exists only for admins; a non-admin gets these two
  // reports as top-level entries instead, behind the same permissions.
  "appointments-report": ADMIN,
  "cancelled-and-no-show-appointments-report": {
    roles: ADMIN_LEVEL_GROUPS,
    permissions: [VIEW_REPORTS]
  },
  "tested-not-sold-report": {
    roles: ADMIN_LEVEL_GROUPS,
    permissions: [VIEW_OWN_REPORTS_ONLY]
  },
  "open-opportunities-report": ADMIN,
  "appointments-from-web-scheduler-report": ADMIN,
  "new-patient-appointments-report": ADMIN,
  "capacity-report": { roles: ADMIN_LEVEL_GROUPS, permissions: [VIEW_REPORTS] },
  "appointment-status-report": ADMIN,
  "appointment-completion-report": ADMIN,
  "physician-referrals-report": ADMIN_OR_ANY_REPORTS_PERMISSION,
  "personal-events-report": ADMIN_OR_ANY_REPORTS_PERMISSION,
  sale: ADMIN_FINANCIAL,
  finances: ADMIN_FINANCIAL,
  "sale-items": ADMIN_FINANCIAL,
  "returned-line-items": ADMIN_FINANCIAL,
  "exchanged-line-items": ADMIN_FINANCIAL,
  "general-ledger-report": ADMIN_FINANCIAL,
  profitability: ADMIN_FINANCIAL,
  "insurer-cpt-code": { ...ADMIN_FINANCIAL, keywords: ["usage"] },
  "patient-cpt-code": { ...ADMIN_FINANCIAL, keywords: ["usage"] },
  "sales-by-zip-code-report": ADMIN_FINANCIAL,
  "sales-by-appointment-type-report": ADMIN_FINANCIAL,
  "sale-tax": { ...ADMIN_FINANCIAL, keywords: ["taxes"] },
  // The section shows when either commissions flag is on, so the legacy
  // listing's own flag must not hide it.
  "commissions-report": {
    roles: ADMIN_LEVEL_GROUPS,
    permissions: [VIEW_OWN_REPORTS_ONLY],
    featureFlag: null,
    when: (access) =>
      canSeeFinancialReports(access) && commissionsEnabled(access)
  },
  "commissions-breakdown": {
    ...ADMIN,
    when: (access) =>
      canSeeFinancialReports(access) && commissionsEnabled(access)
  },
  "opportunity-sales-report": ADMIN_FINANCIAL,
  "opportunity-sales-appointment-type-report": ADMIN_FINANCIAL,
  "staff-metrics-report": {
    roles: ADMIN_LEVEL_GROUPS,
    permissions: [VIEW_OWN_REPORTS_ONLY],
    when: canSeeFinancialReports
  },
  "staff-net-payments-report": ADMIN_FINANCIAL,
  "accounts-receivable-report": ADMIN_FINANCIAL,
  "invoices-receivable": ADMIN_FINANCIAL,
  "insurer-receivable": ADMIN_FINANCIAL,
  "managed-care-receivable": ADMIN_FINANCIAL,
  "managed-care-hearing-aid-units": ADMIN_FINANCIAL,
  "ar-snapshot": ADMIN_FINANCIAL,
  "private-pay-aging": ADMIN_FINANCIAL,
  "managed-care-party-aging": ADMIN_FINANCIAL,
  "accounts-payable-report": {
    ...ADMIN_OR_ANY_REPORTS_PERMISSION,
    featureFlag: ACCOUNTS_PAYABLE_REPORT_VIEW,
    when: canSeeFinancialReports
  },
  payment: ADMIN_FINANCIAL,
  "payment-method-breakdown": ADMIN_FINANCIAL,
  "patient-credits": ADMIN_FINANCIAL,
  "payment-requests": {
    ...ADMIN,
    when: ({ practiceBillingSettings }) =>
      Boolean(practiceBillingSettings?.online_payment_enabled)
  },
  "recurring-payments-report": {
    ...ADMIN,
    when: ({ practiceBillingSettings }) =>
      Boolean(practiceBillingSettings?.subscription_payment_enabled)
  },
  adjustment: ADMIN_FINANCIAL,
  "adjustment-breakdown": ADMIN_FINANCIAL,
  refunds: ADMIN_FINANCIAL,
  "insurer-credit-ledger": { roles: INSURER_CREDIT_LEDGER_GROUPS },
  "revenue-breakdown-report": ADMIN_FINANCIAL,
  "appointments-revenue-by-referral": ADMIN_FINANCIAL,
  "revenue-by-product-type": ADMIN_FINANCIAL,
  "revenue-by-product-category": ADMIN_FINANCIAL,
  "revenue-by-managed-care-report": ADMIN_FINANCIAL,
  "revenue-by-insurer-report": ADMIN_FINANCIAL,
  "patient-product": { ...ADMIN, keywords: ["hearing aids"] },
  "chs-report": ADMIN,
  "order-item": ADMIN_OR_ANY_REPORTS_PERMISSION,
  "claims-report": ADMIN_OR_ANY_REPORTS_PERMISSION,
  "alerts-report": ADMIN_OR_ANY_REPORTS_PERMISSION,
  "unanswered-texts-report": ADMIN_OR_ANY_REPORTS_PERMISSION,
  discarded: ADMIN
};

const notAdmin = (access: DestinationAccess) => !isAdmin(access);

/** Menu entries with no registry row of their own, or at a second URL. */
const UNREGISTERED_REPORT_DESTINATIONS: QuickLauncherDestination[] = [
  {
    section: SECTION,
    to: "patients/birthdate",
    label: "Birthdates",
    trail: ["Patients"],
    roles: ADMIN_LEVEL_GROUPS
  },
  {
    section: SECTION,
    to: "appointment-status/appointment-status-by-referral-source",
    label: "By Referral Source",
    trail: ["Appointment Status"],
    featureFlag: APPOINTMENT_STATUS_BY_REFERRAL_SOURCE_REPORT,
    roles: ADMIN_LEVEL_GROUPS
  },
  {
    section: SECTION,
    to: "appointment-status/appointment-created-status-by-referral-source",
    label: "By Referral Source (Created)",
    trail: ["Appointment Status"],
    featureFlag: APPOINTMENT_CREATED_BY_REFERRAL_SOURCE_REPORT,
    roles: ADMIN_LEVEL_GROUPS
  },
  // Non-admins get the two aging reports at the top level of the menu, where
  // `reports.routes.tsx` mounts a second route for them.
  {
    section: SECTION,
    to: "private-pay-aging",
    label: "Private Pay Aging",
    trail: [],
    featureFlag: PRIVATE_PAY_AGING_REPORT,
    ...ADMIN_OR_ANY_REPORTS_PERMISSION,
    when: (access) => notAdmin(access) && canSeeFinancialReports(access)
  },
  {
    section: SECTION,
    to: "managed-care-aging",
    label: "Managed Care Aging",
    trail: [],
    featureFlag: MANAGED_CARE_AGING_REPORT,
    ...ADMIN_OR_ANY_REPORTS_PERMISSION,
    when: (access) => notAdmin(access) && canSeeFinancialReports(access)
  },
  {
    section: SECTION,
    to: "insurer-credit-ledger/entries",
    label: "Ledger Entries",
    trail: ["Insurance Credit"],
    featureFlag: INSURER_CREDIT_LEDGER,
    roles: INSURER_CREDIT_LEDGER_GROUPS
  },
  {
    section: SECTION,
    to: "revenue-breakdown/clinic",
    label: "By Clinics",
    trail: ["Revenue Breakdown"],
    ...ADMIN_FINANCIAL
  },
  {
    section: SECTION,
    to: "revenue-breakdown/region",
    label: "By Regions",
    trail: ["Revenue Breakdown"],
    roles: ADMIN_LEVEL_GROUPS,
    when: (access) => canSeeFinancialReports(access) && access.hasRegions
  },
  {
    section: SECTION,
    to: "revenue-breakdown/by-provider",
    label: "By Provider",
    trail: ["Revenue Breakdown"],
    featureFlag: REVENUE_BREAKDOWN_BY_PROVIDER_REPORT,
    ...ADMIN_FINANCIAL
  },
  {
    section: SECTION,
    to: "hearing-aids/warranty",
    label: "Warranty Expiration",
    trail: ["Hearing Aids"],
    roles: ADMIN_LEVEL_GROUPS
  },
  {
    section: SECTION,
    to: "hearing-aids/purchase",
    label: "Purchase History",
    trail: ["Hearing Aids"],
    roles: ADMIN_LEVEL_GROUPS
  },
  {
    section: SECTION,
    to: "discarded/appointments",
    label: "Appointments",
    trail: ["Discarded"],
    roles: ADMIN_LEVEL_GROUPS
  },
  ...[
    ["sales", "Sales"],
    ["line-items", "Line Items"],
    ["payments", "Payments"],
    ["adjustments", "Adjustments"],
    ["refunds", "Refunds"]
  ].map(
    ([route, label]): QuickLauncherDestination => ({
      section: SECTION,
      to: `discarded/${route}`,
      label,
      trail: ["Discarded"],
      ...ADMIN_FINANCIAL
    })
  )
];

const registeredReportDestination = (
  reportId: ReportId
): QuickLauncherDestination => {
  const { label, parentId, featureFlag } = REPORT_REGISTRY[reportId];
  const { featureFlag: gateFlag, ...gate } = REPORT_GATES[reportId];
  return {
    section: SECTION,
    to: reportPath(reportId).slice(`${SECTION}/`.length),
    label,
    trail: parentId ? [REPORT_REGISTRY[parentId].label] : [],
    featureFlag: gateFlag === null ? undefined : (gateFlag ?? featureFlag),
    ...gate
  };
};

/**
 * Mirrors `ReportRouteGuard`: a URL resolves to its nearest registered report,
 * whose per-report grant decides; a Financial Reports Only user is also denied
 * any URL that resolves to none, Insurance Credit aside. Checking the URL rather
 * than the row keeps unregistered sub-views on their parent's grant, as the
 * guard and the menu filter both do.
 */
const reportRouteAllows = (path: string, access: DestinationAccess) => {
  const reportId = resolveReportIdFromPathname(path);
  if (reportId) return access.reportAccess[reportId]?.canView !== false;
  return (
    !access.roles.isReportsOnlyUser ||
    isFinancialOnlyAllowedUngovernedPathname(path)
  );
};

const withReportRouteGuard = (
  destination: QuickLauncherDestination
): QuickLauncherDestination => {
  const path = `${SECTION}/${destination.to}`;
  const { when } = destination;
  return {
    ...destination,
    when: (access) =>
      reportRouteAllows(path, access) && (when ? when(access) : true)
  };
};

export const REPORT_DESTINATIONS: QuickLauncherDestination[] = [
  // Nothing type-checks this repo, so a report added to the registry without
  // a gate would otherwise throw at import and take the app down. It is left
  // out instead, and the drift test names it.
  ...REPORT_IDS.filter((reportId) => reportId in REPORT_GATES).map(
    registeredReportDestination
  ),
  ...UNREGISTERED_REPORT_DESTINATIONS
].map(withReportRouteGuard);
